import React, { useEffect } from 'react';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { useAuth } from '@leagueplatform/auth-utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { RewardsEventControllerWrapper } from 'components/rewards-event-controller-wrapper/rewards-event-controller-wrapper.component';
import {
  ACHIEVEMENTS_ENABLED,
  REWARDS_EVENTS_ENABLED,
} from '@leagueplatform/rewards-api';
import { BasePage } from 'components/base-page/base-page.component';
import { PrivateRoutesContainer } from 'components/private-routes-container/private-routes-container.component';
import {
  ALLOWED_UNAUTHENTICATED_ROUTES,
  APP_MANAGED_ROUTES,
  NON_RESTORABLE_ROUTES,
} from 'common/app-managed-routes';
import { capturePostLoginRedirect } from 'utils/post-login-redirect';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';

export const AuthSessionContainer = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const doesNotRequireAuthentication = ALLOWED_UNAUTHENTICATED_ROUTES.some(
    (path) => location.pathname === path,
  );

  const { isAuthenticated, error } = useAuth({
    redirectToLogInWhenSignedOut: !doesNotRequireAuthentication,
  });

  const { data: isRewardsLayerEnabled } = useFeatureFlagQuery(
    REWARDS_EVENTS_ENABLED,
  );

  const { data: isAchievementsEnabled } =
    useFeatureFlagQuery(ACHIEVEMENTS_ENABLED);

  useEffect(() => {
    // Fallback for the deep link: the SDK captures it into appState too, but
    // checkAuth drops appState when a session already exists, so persist the
    // attempted path+query here and restore it after login.
    if (
      isAuthenticated === false &&
      !doesNotRequireAuthentication &&
      !NON_RESTORABLE_ROUTES.includes(location.pathname)
    ) {
      capturePostLoginRedirect(
        location.pathname + location.search + location.hash,
      );
    }
  }, [
    isAuthenticated,
    doesNotRequireAuthentication,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (error) {
      // The session is unusable and can't be recovered in place, so send the
      // user through a fresh login rather than leaving them on a broken page.
      navigate(APP_MANAGED_ROUTES.SIGN_IN, { replace: true });
    }
  }, [error, navigate]);

  if (!isAuthenticated && !doesNotRequireAuthentication)
    return <LoadingIndicator />;

  if (isAuthenticated === false && doesNotRequireAuthentication)
    return <BasePage isPrivatePage={false}>{children}</BasePage>;

  return (
    <PrivateRoutesContainer>
      {isRewardsLayerEnabled && isAchievementsEnabled && (
        <RewardsEventControllerWrapper />
      )}
      {children}
    </PrivateRoutesContainer>
  );
};
